<script setup lang="ts">
// Schedules the guard disabled after four failed runs in a row. Admins can turn
// a schedule back on (once its cause is fixed) or dismiss the alert.
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { scheduleName, useAlerts } from "@/composables/useAlerts";
import { ago } from "@/lib/format";
import Icon from "@/components/ui/Icon.vue";
import ScheduleFix from "./ScheduleFix.vue";

const auth = useAuthStore();
const { open, resolve, refresh } = useAlerts();
const busy = ref<string | null>(null);
const error = ref("");
// fixing a paused schedule's arguments here, not only re-enabling it (2026-10-26)
const fixing = ref<string | null>(null), fixed = ref("");
async function done(message: string) { fixing.value = null; fixed.value = message; await refresh(); }

async function act(id: string, reEnable: boolean) {
  busy.value = id;
  error.value = "";
  try { await resolve(id, reEnable); }
  catch (e) { error.value = (e as Error).message || "Couldn't update the alert."; }
  finally { busy.value = null; }
}
</script>

<template>
  <!-- kept while it says what a fix did: that alert was often the last one open (2026-10-26) -->
  <section v-if="open.length || fixed" class="alerts" role="alert" aria-live="polite">
    <article v-for="a in open" :key="a.id" class="alert panel">
      <Icon name="warning" class="ico" />
      <p class="body">
        <strong>{{ scheduleName(a.schedule_path) }} is {{ a.disabled ? "paused" : "failing" }}.</strong>
        <span class="muted">&nbsp;{{ a.streak }} consecutive failures · {{ ago(a.disabled_at || a.raised_at) }}</span>
        <span v-if="a.last_error" class="err mono" :title="a.last_error"> · {{ a.last_error }}</span>
      </p>
      <div class="row actions">
        <button v-if="auth.isAdmin && a.disabled" class="btn btn--sm btn--primary" :aria-expanded="fixing === a.id"
                @click="fixing = fixing === a.id ? null : a.id; fixed = ''">Fix it</button>
        <button v-if="auth.isAdmin && a.disabled" class="btn btn--sm" :disabled="busy === a.id" @click="act(a.id, true)">Re-enable</button>
        <RouterLink to="/schedules" class="btn btn--sm">Review</RouterLink>
        <button v-if="auth.isAdmin" class="btn btn--sm btn--ghost" :disabled="busy === a.id" @click="act(a.id, false)">Dismiss</button>
      </div>
      <ScheduleFix v-if="fixing === a.id" class="fixer" :alert-id="a.id" @done="done" @cancel="fixing = null" />
    </article>
    <p v-if="fixed" class="inline-ok small" role="status">{{ fixed }}</p>
    <p v-if="error" class="inline-error">{{ error }}</p>
  </section>
</template>

<style scoped>
.alerts { display: flex; flex-direction: column; gap: var(--space-3); }
.alert { display: flex; align-items: center; gap: var(--space-4); padding: var(--space-4) var(--space-5); border-color: color-mix(in srgb, var(--working) 35%, var(--line)); flex-wrap: wrap; }
.fixer { flex: 1 1 100%; }
.inline-ok { color: var(--done); margin: 0; }
.ico { color: var(--working); }
.body { flex: 1; min-width: 0; font-size: var(--text-sm); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.err { font-size: var(--text-xs); color: var(--muted); }
.actions { flex: none; gap: var(--space-2); }
@media (max-width: 720px) {
  .alert { flex-wrap: wrap; align-items: flex-start; }
  .body { white-space: normal; flex: 1 1 calc(100% - 40px); overflow-wrap: anywhere; }
  .err { display: none; }
  .actions { margin-left: var(--space-7); }
}
</style>
